import tinycolor from "tinycolor2";
import { computed, ComputedRef } from "vue";
import { useStore } from "vuex";

import { CustomizationRecord } from "@/Customizations/v2/customizations";
import { Appointment } from "@/typings/scheduler";

// The phone card's V1 default fill per status key (design-contract table,
// CONTEXT.md). Read by getPhoneFillColor as the last resort before a tenant
// customization, and exported so a spec can assert every row without a store.
export const PHONE_V1_STATUS_FILLS: Record<string, string> = {
  pending_unassigned: "#cc0000",
  pending_assigned: "#48b2fe",
  accepted_pre_work: "#276fff",
  accepted: "#19c879",
  running: "#118c4f",
  compeleted_won_opportunity: "#094929",
  compeleted_lost_opportunity: "#fe7c00"
};

// Perceived-brightness threshold (tinycolor2's getBrightness() is 0-255) that
// decides dark-ink versus white card text. Pinned at 153, the "above 60
// percent" the style guide specifies -- deliberately NOT tinycolor2's own
// light/dark helper predicate, whose threshold of 128 would flip #19c879
// against the design's computed intent.
export const PHONE_TEXT_BRIGHTNESS_THRESHOLD = 153;

// The phone path's marker palette: travel/late/hold treatments and the
// calendar-event fills. One table so the whole phone palette has one owner;
// plans 02, 03 and 10 consume the marker half of it.
//
// travelStripe/travelBase, lateStripe/lateBase: the style guide draws travel
// and late as a diagonal-gradient stripe in both its legend and its own
// rendered board mockup, not as the tiled V1 SVG chevron D-07 originally
// specified (see the debug session that overturned D-07,
// tb-2253-chevron-vs-styleguide-stripes.md). Base is the tone the mark reads
// as (the guide's SECOND gradient stop); stripe is the tone drawn over it.
//
// eventFill/eventBorder: V1's own calendar-event treatment
// (app/assets/stylesheets/pages/calendar.scss:336-350's .schedule-event
// rule), copied from the resolved values of
// app/assets/stylesheets/layout/variables.scss lines 67-68
// ($neutral-gray-400 / $neutral-gray-600). A tenant cannot customise
// calendar-event colours -- getPhoneFillColor is keyed on appointment
// status keys only -- so these are fixed values, not a customisation path
// (G-TB-2253-5c).
//
// allDayHatchBase/allDayHatchStripe: the all-day (all-technician, D-08) mark
// must read as its own thing, not as travel time -- the travel chevron and
// the (now-retired) plain "unavailable" hatch were both near-identical
// greys at the same 135deg diagonal, and a day-wide block drawn from that
// family collides with them (G-TB-2253-1c). This pair is a measured choice
// pinned to satisfy that constraint (far lighter than both grey families,
// still textured against itself), not a design-supplied token -- see
// useScheduleColors.spec.ts for the numbers it has to clear.
export const PHONE_MARKER_COLORS = {
  ink: "#1f2733",
  white: "#ffffff",
  travelBase: "#9AA4AB",
  travelStripe: "#D0D7D9",
  lateBase: "#FFDA00",
  lateStripe: "#D9DDE0",
  // The stripe ink. The short-work-block spill rule (MobileAppointmentEvent.vue's
  // scoped style block) now reads this value too, as a second reader
  // alongside the travel/late segment's own `color` binding below --
  // repeated there as a literal, since a CSS container query cannot read a
  // TypeScript constant. Keep the two in sync by hand if this ever changes.
  markerText: "#333333",
  holdFill: "#e1e1e1",
  holdBorder: "#9aa4ab",
  holdText: "#5b6572",
  eventFill: "#E1E2E3",
  eventBorder: "#9CA4AA",
  // getPhoneFillColor's last-resort fallback for an unrecognised status
  // key -- unrelated to calendar events and has no test, so this keeps its
  // pre-existing value under a name that describes what it does instead of
  // the exclusive-event hatch that used to be its other reader.
  unknownStatusFill: "#9CA3AF",
  allDayHatchBase: "#EEF2F8",
  allDayHatchStripe: "#D5DEEC"
};

// The remaining diagonal-hatch marker kinds -- travel, late and the all-day
// calendar event. The exclusive and available calendar events that used to
// share this mechanism (as "unavailable") now carry V1's flat fills
// (PHONE_MARKER_COLORS.eventFill/eventBorder/white) instead: an exclusive
// event's grey hatch sat within a couple of hex digits of the travel band's
// own greys at the same diagonal angle, so the two marks were
// indistinguishable at a glance (G-TB-2253-5c). Travel and late still share
// one composer because the style guide draws both as diagonal stripes in
// its legend and its own rendered board mockup, and a scale-invariant
// gradient has no tile boundary to misalign the way the retired SVG
// chevron did on a tall band.
export type PhoneHatchKind = "all_day" | "travel" | "late";

// Band width (px) each hatch kind repeats at -- travel and late band
// identically at the guide's 7px, and the all-day mark keeps its existing
// looser band as a second, non-colour channel that separates it from the
// travel/late stripes even before colour is considered.
export const PHONE_HATCH_BAND_PX: Record<PhoneHatchKind, number> = {
  all_day: 14,
  travel: 7,
  late: 7
};

// Tone pair per hatch kind, read by phoneMarkerHatch. Module-private: the
// exported surface stays PhoneHatchKind, PHONE_HATCH_BAND_PX and
// phoneMarkerHatch.
const HATCH_TONES: Record<PhoneHatchKind, { stripe: string; base: string }> = {
  all_day: {
    stripe: PHONE_MARKER_COLORS.allDayHatchStripe,
    base: PHONE_MARKER_COLORS.allDayHatchBase
  },
  travel: {
    stripe: PHONE_MARKER_COLORS.travelStripe,
    base: PHONE_MARKER_COLORS.travelBase
  },
  late: {
    stripe: PHONE_MARKER_COLORS.lateStripe,
    base: PHONE_MARKER_COLORS.lateBase
  }
};

// The single owner of every diagonal-hatch gradient string on the phone
// card. A pure function over module constants only -- nothing
// caller-supplied reaches the composed CSS string (T-TB2253-17) -- so
// MobileAppointmentEvent.vue's markerStyle and segmentStyle never inline a
// gradient literal and a spec can assert the output directly.
export const phoneMarkerHatch = (kind: PhoneHatchKind): string => {
  const band = PHONE_HATCH_BAND_PX[kind];
  const { stripe, base } = HATCH_TONES[kind];
  return `repeating-linear-gradient(135deg, ${stripe} 0 ${band}px, ${base} ${band}px ${band * 2}px)`;
};

export const useScheduleColors = () => {
  const store = useStore();
  const eventStatuses = {
    traveling: "traveling",
    late: "late",
    hold: "hold",
    available: "available_event",
    unavailable: "unavailable_event",
    accepted_pre_work: "accepted_pre_work"
  };

  const statusCustomizations: ComputedRef<{
    [k: string]: CustomizationRecord;
  }> = computed(() => {
    return store.getters.sortedAppointmentStatusCustomizations;
  });
  const getStatusColor = (status) => {
    switch (status) {
      case eventStatuses.accepted_pre_work:
        return "#44d2d5";
      case eventStatuses.traveling:
        return "#6875F5";
      case eventStatuses.late:
        return "#C81E1E";
      case eventStatuses.hold:
        return "#9CA3AF";
      case eventStatuses.available:
      case eventStatuses.unavailable:
        return "#9CA3AF";
      default:
        return (
          statusCustomizations.value[status]?.value ||
          statusCustomizations.value[status]?.default
        );
    }
  };
  const getBGColor = (status: string) => {
    const hex = getStatusColor(status);
    let lightenPct = 50;
    const color = tinycolor(hex);
    let lighterShade = color.lighten(lightenPct);
    let brightness = lighterShade.getBrightness();
    do {
      lightenPct = lightenPct - 2;
      lighterShade = tinycolor(hex).lighten(lightenPct);
      brightness = lighterShade.getBrightness();
    } while (lighterShade.toHexString() === "#ffffff" || brightness > 245);
    return lighterShade.toHexString();
  };
  const getTextColor = (status: string) => {
    const hex = getStatusColor(status);
    return tinycolor(hex).darken(30).toHexString();
  };
  const getEventStyles = (
    appointment?: Appointment,
    ghost?: boolean
  ): {
    classes: Array<string>;
    colors: { background: string; text: string; status: string };
  } => {
    const customizationName = getStatusCustomizationName(appointment, ghost);

    const eventStyles = {
      classes: [
        "px-2 py-1 text-xs hover:ring-1 rounded-lg hover:shadow"
      ] as string[],
      colors: {
        background: getBGColor(customizationName),
        text: getTextColor(customizationName),
        status: getStatusColor(customizationName)
      }
    };
    if (
      [eventStatuses.available, eventStatuses.hold].includes(customizationName)
    ) {
      eventStyles.classes.push("outline-dashed hover:outline-solid");
    }
    return eventStyles;
  };
  const getStatusCustomizationName = (
    appointment?: Appointment,
    ghost?: boolean
  ): string => {
    if (!appointment) {
      return eventStatuses.unavailable;
    }
    if (ghost) {
      return eventStatuses.hold;
    }
    switch (appointment.status) {
      case "completed":
        return appointment.job_opportunity_status === "lost"
          ? "compeleted_lost_opportunity"
          : "compeleted_won_opportunity";
      case "accepted": {
        if (appointment.travel_start_time) {
          return appointment.late ? "late" : "traveling";
        }
        return "accepted_pre_work";
      }
      case "pending": {
        return appointment.unassigned
          ? "pending_unassigned"
          : "pending_assigned";
      }
      default:
        // Running
        return appointment.status;
    }
  };

  // Phone-only, additive helpers. None of these delegate to getStatusColor:
  // that function short-circuits several keys (accepted_pre_work included) to
  // fixed desktop hexes before it ever reads the tenant's customization, which
  // D-02 forbids on the phone path -- the phone card must resolve tenant
  // customization first and the V1 default second for every status key.

  // Normalises getStatusCustomizationName's marker names down to the status
  // whose fill they borrow: on the phone, travel and late are their own
  // chevron segment (plan 02), so the work block underneath keeps the Start
  // Trip colour rather than a marker colour.
  const getPhoneWorkStatusKey = (
    appointment?: Appointment,
    ghost?: boolean
  ): string => {
    const key = getStatusCustomizationName(appointment, ghost);
    if (key === eventStatuses.traveling || key === eventStatuses.late) {
      return "accepted";
    }
    return key;
  };

  // Tenant value, then tenant default, then the V1 default -- never the fixed
  // desktop hex getStatusColor would return for the same key.
  const getPhoneFillColor = (statusKey: string): string => {
    return (
      statusCustomizations.value[statusKey]?.value ||
      statusCustomizations.value[statusKey]?.default ||
      PHONE_V1_STATUS_FILLS[statusKey] ||
      PHONE_MARKER_COLORS.unknownStatusFill
    );
  };

  // Ink above the brightness threshold, white otherwise -- except
  // pending_assigned on V1's own fill, which the style guide pins to white
  // regardless of computed brightness (D-05's hard exception).
  //
  // The exception is scoped to that FILL, not to the status. #48b2fe scores
  // 155, two points over the threshold, which is the whole reason D-05 needs
  // an exception at all -- but getPhoneFillColor reads the tenant's own status
  // customizations ahead of PHONE_V1_STATUS_FILLS, so a tenant who recolours
  // pending_assigned to anything pale would otherwise get white text on it.
  // That is the unreadable case the brightness rule exists to prevent, and the
  // exception must not reintroduce it on the one status it covers.
  const getPhoneTextColor = (fillHex: string, statusKey?: string): string => {
    const isV1PendingAssignedFill =
      tinycolor(fillHex).toHexString() ===
      tinycolor(PHONE_V1_STATUS_FILLS.pending_assigned).toHexString();
    if (statusKey === "pending_assigned" && isV1PendingAssignedFill) {
      return PHONE_MARKER_COLORS.white;
    }
    return tinycolor(fillHex).getBrightness() > PHONE_TEXT_BRIGHTNESS_THRESHOLD
      ? PHONE_MARKER_COLORS.ink
      : PHONE_MARKER_COLORS.white;
  };

  return {
    statusCustomizations,
    getStatusColor,
    getBGColor,
    getTextColor,
    getEventStyles,
    getStatusCustomizationName,
    getPhoneWorkStatusKey,
    getPhoneFillColor,
    getPhoneTextColor
  };
};
